<template>
  <form-item
    v-if="isForm"
    :label="label"
    :prop="name"
    :rules="rules"
    class="img-uploader"
    :hasDesc="descContent !== ''"
    :name="name"
    :descContent="descContent"
    >
    <el-upload
      v-loading="isLoading"
      drag
      action="./cover_image"
      :before-upload="beforeUpload"
      :show-file-list="false"
      :class="uploadClass"
      >
      <div v-if="formData[name]">
        <img
          :src="getImgURL(formData[name])"
          :class="imgClass"
          @load="onLoad"
          />
      </div>
      <div v-else>
        <i class="el-icon-upload"></i>
        <div class="el-upload__text" v-html="$t('api.upload.dropmsg')" />
      </div>
      <div class="el-upload__tip" slot="tip">
        <el-alert
          v-if="sizeLimit > 0"
          :title="sizeLimitText"
          type="info"
          :closable="false"
          show-icon
          >
        </el-alert>
        <el-alert
          v-if="showRatioTip"
          :title="ratioTipText"
          type="info"
          :closable="false"
          show-icon
          >
        </el-alert>
        <div
          class="img-info"
          v-if="imageLoaded"
          >
          <div
            v-if="image.size > 0"
            >
            {{ $t('api.default.fileSize.label') }}: {{ bytes(image.size) }}
          </div>
          <el-alert
            v-if="isWarnSize"
            :title="warnSizeText"
            type="warning"
            :closable="false"
            show-icon
            >
          </el-alert>
          <div>
            {{ $t('api.default.imgRatio.label') }}: {{ image.width }} x {{ image.height }}
          </div>
          <el-alert
            v-if="isWarnRatio"
            :title="warnRatioText"
            type="warning"
            :closable="false"
            show-icon
            >
          </el-alert>
        </div>
        <div
          class="img-info"
          v-if="formData[name]"
          >
          <i class="el-icon-picture"></i>
          {{ getURLString(formData[name]) }}
        </div>
      </div>
    </el-upload>
    <el-input
      v-if="dataId=='0'"
      v-show="false"
      v-model.trim="formData[name]"
      class="form-item"
      readonly>
    </el-input>
  </form-item>
  <el-table-column
    v-else
    align="center"
    :label="$t('api.image')"
    width="75">
    <template slot-scope="scope">
      <el-tooltip
        v-if="scope.row[name]"
        class="item"
        effect="dark"
        placement="right"
        :content="getURLString(scope.row[name])">
        <img
          class="list-thumbnail"
          :key="scope.row[idField]"
          :alt="scope.row[nameField]"
          :title="scope.row[nameField]"
          :src="getThumbnail(scope.row[name])"
          :data-source="getImgURL(scope.row[name])"
          @click="showViewer(scope.row[idField], scope.$index)"
          />
      </el-tooltip>
      <el-tooltip
        v-else
        class="item"
        effect="dark"
        placement="right"
        :content="$t('api.noImage')">
        <i class="el-icon-picture"></i>
      </el-tooltip>
    </template>
  </el-table-column>
</template>

<script>

import bytes from 'bytes'
import { decimalFormat } from '@/utils/number'
import { getTransText, transText } from '@/utils/i18n'
import { getURLString, getImgURL, getThumbnail } from '@/utils/path'

export default {
  name: 'cover-img',
  props: {
    name: {
      type: String,
      default: 'cover_image'
    },
    base: {
      type: String,
      default: ''
    },
    formData: {
      type: Object,
      default: () => {}
    },
    uploadImage: {
      type: Function
    },
    dataId: {
      type: String
    },
    isForm: {
      type: Boolean,
      default: true
    },
    idField: {
      type: String,
      default: 'id'
    },
    nameField: {
      type: String,
      default: 'name'
    },
    labelText: {
      type: String,
      default: ''
    },
    uploadClass: {
      type: String,
      default: 'cover-uploader'
    },
    imgClass: {
      type: String,
      default: 'img'
    },
    showViewer: {
      type: Function,
      default: () => {}
    },
    hasDesc: {
      type: Boolean,
      default: false
    },
    sizeLimit: {
      type: Number,
      default: 0
    },
    warnSizeLimit: {
      type: Number,
      default: 0
    },
    warnRatioWidth: {
      type: Number,
      default: 0
    },
    warnRatioHeight: {
      type: Number,
      default: 0
    },
    showRatioTip: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      id: this.dataId,
      isLoading: false,
      image: {
        size: '',
        height: '',
        width: ''
      },
      imageLoaded: false,
      fileList: []
    }
  },
  computed: {
    descContent () {
      let res = ''
      if (this.hasDesc) {
        res = this.getTransText(this.name, 'desc', this.base)
      }
      if (this.warnSizeLimit !== 0) {
        res = this.appendRes(res, this.warnSizeText)
      }
      if (this.warnRatioWidth !== 0 || this.warnRatioHeight !== 0) {
        res = this.appendRes(res, this.warnRatioText)
      }
      return res
    },
    warnSizeText () {
      return this.$t('api.default.imageFileSize.warning', {
        size: bytes(this.warnSizeLimit)
      })
    },
    warnRatioText () {
      return this.$t('api.default.imageRatio.warning', {
        width: this.warnRatioWidth,
        height: this.warnRatioHeight
      })
    },
    sizeLimitText () {
      return this.transText('fileSizeLimit', {size: bytes(this.sizeLimit)})
    },
    isWarnSize () {
      return (this.warnSizeLimit !== 0 && this.image.size >= this.warnSizeLimit)
    },
    ratioTipText () {
      return this.$t('api.default.imageRatioTip.label', {
        ratioWidth: this.decimalFormat(this.warnRatioWidth / this.warnRatioHeight, 2),
        ratioHeight: 1,
        width: this.warnRatioWidth,
        height: this.warnRatioHeight
      })
    },
    isWarnRatio () {
      return (this.warnRatioWidth !== 0 && this.warnRatioHeight !== 0 &&
        this.imageLoaded &&
        this.decimalFormat(this.image.width / this.image.height, 2) !==
        this.decimalFormat(this.warnRatioWidth / this.warnRatioHeight, 2)
      )
    },
    label () {
      return this.labelText || this.$t('api.coverImage.label')
    },
    rules () {
      return (this.required) ? [{
        required: true,
        message: this.getTransText('default', 'errImg'),
        trigger: 'blur'
      }] : this.$attrs.rules
    }
  },
  created () {
  },
  mounted () {
  },
  methods: {
    bytes,
    decimalFormat,
    getImgURL,
    getThumbnail,
    getURLString,
    getTransText,
    transText,
    appendRes (res, str) {
      if (res !== '') {
        res += '<br/>'
      }
      return res + str
    },
    validateFileSize (file) {
      return this.sizeLimit === 0 || file.size <= this.sizeLimit
    },
    onLoad (e) {
      this.image.width = e.target.naturalWidth
      this.image.height = e.target.naturalHeight
      this.imageLoaded = true
    },
    beforeUpload (file) {
      if (!this.validateFileSize(file)) {
        this.$message.error(this.$t('api.fileSizeNotMatch'))
        return false
      }

      this.image.size = file.size
      let reader = new FileReader()

      reader.readAsDataURL(file)
      reader.onload = evt => {
        let img = new Image()
        img.onload = () => {
          this.image.width = img.width
          this.image.height = img.height
          this.imageLoaded = true
        }
        img.src = evt.target.result
      }

      let data = new FormData()
      data.append('files', file, file.name)
      let config = {
        // headers: {'Content-Type': 'multipart/form-data'}
      }
      this.isLoading = true
      this.uploadImage(this.id, data, config).then(response => {
        this.formData[this.name] = response.data[this.name]
        this.$message({
          message: this.$t('api.imgUploadSuccess'),
          type: 'success',
          duration: 2000
        })
      }).catch(err => {
        console.log(err)
      }).then(() => {
        this.isLoading = false
      })
      return false
    }
  },
  watch: {
    dataId (newId) {
      this.id = newId
    }
  }
}
</script>

<style scoped>
.size-limit-hint {
  color: blue;
}
</style>
